import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { localDateFromYMD } from '../utils/dbTime';

function MaintenanceModal({ onClose }) {
  const { isOwner } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [newRecord, setNewRecord] = useState({
    maintenance_type: 'oil_change',
    description: '',
    performed_at_km: '',
    performed_date: new Date().toISOString().split('T')[0],
    cost: '',
    next_due_km: ''
  });

  const maintenanceTypes = [
    { value: 'oil_change', label: 'Oil Change', interval: 8000 },
    { value: 'tire_rotation', label: 'Tire Rotation', interval: 12000 },
    { value: 'brake_inspection', label: 'Brake Inspection', interval: 20000 },
    { value: 'transmission_service', label: 'Transmission Service', interval: 50000 },
    { value: 'air_filter', label: 'Air Filter', interval: 25000 },
    { value: 'fuel_filter', label: 'Fuel Filter', interval: 40000 },
    { value: 'coolant_flush', label: 'Coolant Flush', interval: 50000 },
    { value: 'inspection', label: 'Safety Inspection', interval: 0 },
    { value: 'custom', label: 'Custom/Other', interval: 0 }
  ];

  useEffect(() => {
    fetchTrucks();
  }, []);

  useEffect(() => {
    if (selectedTruck) {
      fetchMaintenanceRecords(selectedTruck.id);
    }
  }, [selectedTruck]);

  const fetchTrucks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trucks')
        .select('*')
        .eq('is_active', true)
        .order('truck_number');

      if (error) throw error;
      setTrucks(data || []);
      if (data && data.length > 0) {
        setSelectedTruck(data[0]);
      }
    } catch (error) {
      console.error('Error fetching trucks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceRecords = async (truckId) => {
    try {
      const { data, error } = await supabase
        .from('maintenance_records')
        .select('*')
        .eq('truck_id', truckId)
        .order('performed_date', { ascending: false });

      if (error) throw error;
      setMaintenanceRecords(data || []);
    } catch (error) {
      console.error('Error fetching maintenance records:', error);
    }
  };

  const handleAddRecord = async () => {
    if (!selectedTruck) return;

    try {
      const { error } = await supabase
        .from('maintenance_records')
        .insert([{
          truck_id: selectedTruck.id,
          maintenance_type: newRecord.maintenance_type,
          description: newRecord.description,
          performed_at_km: parseInt(newRecord.performed_at_km) || selectedTruck.current_odometer_km,
          performed_date: newRecord.performed_date,
          cost: parseFloat(newRecord.cost) || null,
          next_due_km: parseInt(newRecord.next_due_km) || null
        }]);

      if (error) throw error;

      // Update truck odometer if the maintenance was done at a higher KM
      if (parseInt(newRecord.performed_at_km) > selectedTruck.current_odometer_km) {
        await supabase
          .from('trucks')
          .update({ current_odometer_km: parseInt(newRecord.performed_at_km) })
          .eq('id', selectedTruck.id);
      }

      setShowAddForm(false);
      setNewRecord({
        maintenance_type: 'oil_change',
        description: '',
        performed_at_km: '',
        performed_date: new Date().toISOString().split('T')[0],
        cost: '',
        next_due_km: ''
      });
      fetchMaintenanceRecords(selectedTruck.id);
      fetchTrucks();
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      setMaintenanceMsg({ type: 'error', text: 'Failed to add record: ' + error.message });
    }
  };

  const getMaintenanceStatus = (truck) => {
    const alerts = [];
    
    maintenanceTypes.forEach(type => {
      if (type.interval === 0) return;
      
      const lastRecord = maintenanceRecords.find(r => r.maintenance_type === type.value);
      const lastKm = lastRecord?.performed_at_km || 0;
      const nextDueKm = lastRecord?.next_due_km || (lastKm + type.interval);
      const kmUntilDue = nextDueKm - truck.current_odometer_km;
      
      if (kmUntilDue <= 0) {
        alerts.push({ type: type.label, status: 'overdue', kmUntilDue, nextDueKm });
      } else if (kmUntilDue <= 500) {
        alerts.push({ type: type.label, status: 'warning', kmUntilDue, nextDueKm });
      }
    });
    
    return alerts;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overdue': return 'var(--color-danger)';
      case 'warning': return 'var(--color-warning)';
      default: return 'var(--color-success)';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const formatDate = (dateString) => {
    // __TZ_PICKER__ performed_date / due_date are calendar dates; new Date('YYYY-MM-DD')
    // is UTC midnight and displayed as the previous day in Ontario.
    const d = localDateFromYMD(dateString);
    if (!d) return '-';
    return d.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTruckAlerts = () => {
    const allAlerts = [];
    trucks.forEach(truck => {
      const alerts = getMaintenanceStatus(truck);
      alerts.forEach(alert => {
        allAlerts.push({ ...alert, truck });
      });
    });
    return allAlerts.sort((a, b) => a.kmUntilDue - b.kmUntilDue);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)',
        borderRadius: '10px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--surface-2)'
        }}>
          <div>
            <h2 style={{ color: 'var(--accent-primary)', margin: '0 0 0.25rem 0' }}>
              Fleet Maintenance
            </h2>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem' }}>
              Track maintenance schedules and service history
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            x
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'overview' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'history' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Service History
          </button>
          <button
            onClick={() => setActiveTab('alerts')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'alerts' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Alerts
            {getTruckAlerts().length > 0 && (
              <span style={{
                backgroundColor: 'var(--color-danger)',
                color: 'var(--text-primary)',
                padding: '0.1rem 0.5rem',
                borderRadius: '10px',
                fontSize: '0.75rem'
              }}>
                {getTruckAlerts().length}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              Loading...
            </div>
          ) : activeTab === 'overview' ? (
            <div>
              {/* Truck selector */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                  Select Truck
                </label>
                <select
                  value={selectedTruck?.id || ''}
                  onChange={(e) => setSelectedTruck(trucks.find(t => t.id === e.target.value))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)',
                    fontSize: '1rem'
                  }}
                >
                  {trucks.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      Truck {truck.truck_number} - {truck.license_plate} ({truck.current_odometer_km?.toLocaleString()} km)
                    </option>
                  ))}
                </select>
              </div>

              {selectedTruck && (
                <>
                  {/* Truck info card */}
                  <div style={{
                    backgroundColor: 'var(--surface-2)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                      <div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>TRUCK NUMBER</div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                          {selectedTruck.truck_number}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>LICENSE PLATE</div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem' }}>
                          {selectedTruck.license_plate}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>ODOMETER</div>
                        <div style={{ color: 'var(--accent-primary)', fontSize: '1.1rem', fontWeight: 'bold' }}>
                          {selectedTruck.current_odometer_km?.toLocaleString()} km
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>FUEL TYPE</div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '1.1rem', textTransform: 'capitalize' }}>
                          {selectedTruck.fuel_type}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Maintenance status */}
                  <div style={{ marginBottom: '1rem' }}>
                    <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem 0' }}>Maintenance Status</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                      {maintenanceTypes.filter(t => t.interval > 0).map(type => {
                        const lastRecord = maintenanceRecords.find(r => r.maintenance_type === type.value);
                        const lastKm = lastRecord?.performed_at_km || 0;
                        const nextDueKm = lastRecord?.next_due_km || (lastKm + type.interval);
                        const kmUntilDue = nextDueKm - selectedTruck.current_odometer_km;
                        const status = kmUntilDue <= 0 ? 'overdue' : kmUntilDue <= 500 ? 'warning' : 'ok';
                        
                        return (
                          <div
                            key={type.value}
                            style={{
                              backgroundColor: 'var(--bg-primary)',
                              borderRadius: '8px',
                              padding: '0.75rem',
                              borderLeft: `4px solid ${getStatusColor(status)}`
                            }}
                          >
                            <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                              {type.label}
                            </div>
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                              Last: {lastRecord ? `${lastKm.toLocaleString()} km` : 'Never'}
                            </div>
                            <div style={{ 
                              color: getStatusColor(status), 
                              fontSize: '0.85rem',
                              fontWeight: status !== 'ok' ? 'bold' : 'normal'
                            }}>
                              {status === 'overdue' 
                                ? `Overdue by ${Math.abs(kmUntilDue).toLocaleString()} km`
                                : status === 'warning'
                                ? `Due in ${kmUntilDue.toLocaleString()} km`
                                : `Due at ${nextDueKm.toLocaleString()} km`
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Add maintenance button */}
                  <button
                    onClick={() => setShowAddForm(true)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--btn-primary-bg)',
                      color: 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    + Add Maintenance Record
                  </button>
                </>
              )}
            </div>
          ) : activeTab === 'history' ? (
            <div>
              {/* Truck selector */}
              <div style={{ marginBottom: '1rem' }}>
                <select
                  value={selectedTruck?.id || ''}
                  onChange={(e) => setSelectedTruck(trucks.find(t => t.id === e.target.value))}
                  style={{
                    padding: '0.5rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)'
                  }}
                >
                  {trucks.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      Truck {truck.truck_number}
                    </option>
                  ))}
                </select>
              </div>

              {/* Service history table */}
              {maintenanceRecords.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  No maintenance records found for this truck
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--surface-2)' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Date</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Type</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Odometer</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Cost</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Next Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maintenanceRecords.map(record => (
                      <tr key={record.id} style={{ borderBottom: '1px solid var(--surface-2)' }}>
                        <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                          {formatDate(record.performed_date)}
                        </td>
                        <td style={{ padding: '0.75rem', color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                          {record.maintenance_type.replace(/_/g, ' ')}
                        </td>
                        <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                          {record.performed_at_km?.toLocaleString()} km
                        </td>
                        <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                          {formatCurrency(record.cost)}
                        </td>
                        <td style={{ padding: '0.75rem', color: 'var(--text-muted)' }}>
                          {record.next_due_km ? `${record.next_due_km.toLocaleString()} km` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            /* Alerts tab */
            <div>
              {getTruckAlerts().length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#9989;</div>
                  <p>All trucks are up to date on maintenance!</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {getTruckAlerts().map((alert, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: 'var(--surface-2)',
                        borderRadius: '8px',
                        padding: '1rem',
                        borderLeft: `4px solid ${getStatusColor(alert.status)}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <span style={{ color: getStatusColor(alert.status), fontWeight: 'bold' }}>
                            {alert.status === 'overdue' ? '!' : '!'}
                          </span>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                            Truck {alert.truck.truck_number}
                          </span>
                          <span style={{ color: 'var(--text-muted)' }}>-</span>
                          <span style={{ color: 'var(--text-primary)' }}>{alert.type}</span>
                        </div>
                        <div style={{ color: getStatusColor(alert.status), fontSize: '0.9rem' }}>
                          {alert.status === 'overdue'
                            ? `Overdue by ${Math.abs(alert.kmUntilDue).toLocaleString()} km`
                            : `Due in ${alert.kmUntilDue.toLocaleString()} km`
                          }
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedTruck(alert.truck);
                          setShowAddForm(true);
                          setNewRecord(prev => ({
                            ...prev,
                            maintenance_type: maintenanceTypes.find(t => t.label === alert.type)?.value || 'custom'
                          }));
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'var(--btn-primary-bg)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Log Service
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Record Form Modal */}
        {showAddForm && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              backgroundColor: 'var(--surface-1)',
              borderRadius: '10px',
              padding: '1.5rem',
              width: '90%',
              maxWidth: '500px'
            }}>
              <h3 style={{ color: 'var(--accent-primary)', margin: '0 0 1rem 0' }}>Add Maintenance Record</h3>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                  Maintenance Type
                </label>
                <select
                  value={newRecord.maintenance_type}
                  onChange={(e) => {
                    const type = maintenanceTypes.find(t => t.value === e.target.value);
                    setNewRecord({
                      ...newRecord,
                      maintenance_type: e.target.value,
                      next_due_km: type?.interval 
                        ? (parseInt(newRecord.performed_at_km) || selectedTruck?.current_odometer_km || 0) + type.interval
                        : ''
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)'
                  }}
                >
                  {maintenanceTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                  Date Performed
                </label>
                <input
                  type="date"
                  value={newRecord.performed_date}
                  onChange={(e) => setNewRecord({ ...newRecord, performed_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                  Odometer (km)
                </label>
                <input
                  type="number"
                  value={newRecord.performed_at_km}
                  onChange={(e) => setNewRecord({ ...newRecord, performed_at_km: e.target.value })}
                  placeholder={selectedTruck?.current_odometer_km?.toString()}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                  Cost (CAD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newRecord.cost}
                  onChange={(e) => setNewRecord({ ...newRecord, cost: e.target.value })}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                  Next Due (km)
                </label>
                <input
                  type="number"
                  value={newRecord.next_due_km}
                  onChange={(e) => setNewRecord({ ...newRecord, next_due_km: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                  Notes
                </label>
                <textarea
                  value={newRecord.description}
                  onChange={(e) => setNewRecord({ ...newRecord, description: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowAddForm(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#7f8c8d',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRecord}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'var(--btn-primary-bg)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Save Record
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MaintenanceModal;
